import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Button, CircularProgress, Dialog, DialogActions, 
  DialogContent, DialogTitle, Checkbox, FormControlLabel, TextField, 
  Tabs, Tab, MenuItem, FormHelperText
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import { useToast } from "../../components/ToastContext";
import { useSelectedProvider } from "../../services/selectedProiderContext";
import SearchComponent from '../../components/Search';
import { filterRowsByColumns } from '../../Utils/gridSearch';
import StatusFilterHeader, { applyStatusFilter } from '../../components/StatusFilterHeader';

import CloudFormationIcn from "../../assets/CloudFormation.svg";
import TerraformIcn from "../../assets/terraform.svg";
import BicepIcn from "../../assets/Bicep.svg";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`account-tabpanel-${index}`}
      aria-labelledby={`account-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === index && (
        <Box sx={{ pt: 1 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const maskId = (id) => {
  return id ? `${String(id).substring(0, 3)}...${String(id).slice(-3)}` : "N/A";
};

const providerHandlers = {
  aws: (selectedProject) => selectedProject.accountId,
  azure: (selectedProject) => selectedProject.subscriptionId,
  gcp: (selectedProject) => selectedProject.projectId,
};

function CloudSetup() {
  const toast = useToast();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  
  const userId = sessionStorage.getItem('userId'); 
  const userRole = sessionStorage.getItem("rolename") || sessionStorage.getItem("userRole");
  const { selectAccount } = useSelectedProvider();

  const [activeTab, setActiveTab] = useState(0);
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Per-tab status filters ('all' | 'active' | 'inactive')
  const [awsStatusFilter, setAwsStatusFilter] = useState('all');
  const [gitStatusFilter, setGitStatusFilter] = useState('all');
  const [ppStatusFilter, setPpStatusFilter] = useState('all');

  const icons = {
    cloudformation: CloudFormationIcn,
    terraform: TerraformIcn,
    bicep: BicepIcn,
  };

  const getIcon = (icntype) => {
    return icons[icntype.split(' ').join('').toLowerCase()] || ""
  };

  const [clients, setClients] = useState([]);
  const [cloudProviders, setCloudProviders] = useState([]);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const clientResponse = await api.get(`/Client/GetOrganizationDetailsByUserId/${userId}`);
        setClients(clientResponse.data || []);
        const providerResponse = await api.get('/Client/cloudProviders');
        setCloudProviders(providerResponse.data || []);
      } catch (error) {
        console.error('Error fetching global dropdowns:', error);
      }
    };
    fetchDropdowns();
  }, [userId]);

  // Only active organizations should appear in the dropdowns
  const activeClients = useMemo(
    () => clients.filter((client) => client.isActive === true),
    [clients]
  );


  /* =========================================================================================
     1. AWS ACCOUNT DETAILS
  ========================================================================================= */
  const [awsAccounts, setAwsAccounts] = useState([]);
  const [awsLoading, setAwsLoading] = useState(false);
  const [awsSearchTerm, setAwsSearchTerm] = useState('');
  const [awsDialogOpen, setAwsDialogOpen] = useState(false);
  const [awsEditId, setAwsEditId] = useState(null); 
  const [awsFormData, setAwsFormData] = useState({
    cloudProvider: '', accountId: '', accountName: '', roleName: '', environment: '', tenantId: '', applicationClientId: '', isAssumeRole: false, isActive: true,
  });
  const [awsErrors, setAwsErrors] = useState({});

  const azureProviderValue = '1';
  const awsProviderValue = '2';
  const vmwareProviderLabel = 'vmware';

  const accountCloudProviders = useMemo(
    () => cloudProviders.filter((provider) => {
      const providerValue = String(provider?.value || '');
      const providerText = String(provider?.text || '').toLowerCase();
      return [azureProviderValue, awsProviderValue].includes(providerValue) || providerText === vmwareProviderLabel;
    }),
    [cloudProviders]
  );

  const fetchAwsAccounts = async () => {
    setAwsLoading(true);
    try {
      const response = await api.get('/AccountSettingDetails/GetAllCloudAccountDetails', {
        headers: { 'accept': 'text/plain', 'X-CORRELATION-ID': '1d627632-a60c-424b-b59e-dac9d8fc7d99' }
      });
      setAwsAccounts(response.data || []);
    } catch (error) {
      console.error('Error fetching AWS Accounts:', error);
    } finally {
      setAwsLoading(false);
    }
  };

  const handleAwsAdd = () => {
    setAwsEditId(null);
    setAwsFormData({ cloudProvider: '', accountId: '', accountName: '', roleName: '', environment: '', tenantId: '', applicationClientId: '', isAssumeRole: false, isActive: true });
    setAwsErrors({}); 
    setAwsDialogOpen(true);
  };

  const handleAwsEditClick = async (row) => {
    setAwsEditId(row.cloudAccountDetailId);
    setAwsFormData({
      cloudProvider: row.cloudProvider !== undefined && row.cloudProvider !== null ? String(row.cloudProvider) : '',
      accountId: row.accountId || row.awsAccountId || '',
      accountName: row.accountName || '',
      roleName: row.roleName || '',
      environment: row.environment || '',
      tenantId: row.tenantId || '',
      applicationClientId: row.applicationClientId || '',
      isAssumeRole: row.isAssumeRole || false,
      isActive: row.isActive !== undefined ? row.isActive : true,
    });
    setAwsErrors({}); 
    setAwsDialogOpen(true);

    try {
      const response = await api.get(`/AccountSettingDetails/GetCloudAccountDetailsByCloudAccountDetailId/${row.cloudAccountDetailId}`, {
        headers: { 'accept': 'text/plain', 'X-CORRELATION-ID': '1d627632-a60c-424b-b59e-dac9d8fc7d99' }
      });
      
      const data = response.data;
      
      setAwsFormData({
        cloudProvider: data.cloudProvider !== undefined && data.cloudProvider !== null ? String(data.cloudProvider) : '',
        accountId: data.accountId || data.awsAccountId || '',
        accountName: data.accountName || '',
        roleName: data.roleName || '',
        environment: data.environment || '',
        tenantId: data.tenantId || '',
        applicationClientId: data.applicationClientId || '',
        isAssumeRole: data.isAssumeRole || false,
        isActive: data.isActive !== undefined ? data.isActive : true,
      });
    } catch (error) {
      toast.error("Failed to fetch fresh account details");
    }
  };

  const handleAwsSave = async () => {
    setAwsErrors({});
    let errors = {};
    const isAzureProvider = String(awsFormData.cloudProvider) === azureProviderValue;

    if (!awsFormData.cloudProvider) errors.cloudProvider = "Cloud Provider is required";
    if (!awsFormData.accountId) errors.accountId = isAzureProvider ? "Account ID/Subscription ID is required" : "Account ID is required";
    if (!awsFormData.accountName) errors.accountName = "Account Name is required";
    if (isAzureProvider && !awsFormData.tenantId) errors.tenantId = "Tenant ID is required";
    if (isAzureProvider && !awsFormData.applicationClientId) errors.applicationClientId = "Application Client ID is required";

    if (Object.keys(errors).length > 0) {
      setAwsErrors(errors);
      toast.error("Please fill in required fields");
      return;
    }

    setAwsLoading(true);
    try {
      const isAzureProvider = String(awsFormData.cloudProvider) === azureProviderValue;
      const payload = {
        cloudProvider: Number(awsFormData.cloudProvider),
        accountId: awsFormData.accountId,
        accountName: awsFormData.accountName,
        roleName: awsFormData.roleName,
        isAssumeRole: awsFormData.isAssumeRole,
        tenantId: isAzureProvider ? awsFormData.tenantId : null,
        applicationClientId: isAzureProvider ? awsFormData.applicationClientId : null,
        environment: awsFormData.environment,
        isActive: awsFormData.isActive,
        createdBy: Number.parseInt(userId) || 0,
        updatedBy: Number.parseInt(userId) || 0
      };
      const headers = { 'accept': 'text/plain', 'X-CORRELATION-ID': '967fdea3-37a6-4a89-a1a1-baf86a85ecd6', 'Content-Type': 'application/json' };

      if (awsEditId) {
        await api.post(`/AccountSettingDetails/UpdateCloudAccountDetails/${awsEditId}`, payload, { headers });
        toast.success("Account updated successfully!");
      } else {
        await api.post(`/AccountSettingDetails/AddCloudAccountDetails`, payload, { headers });
        toast.success("Account added successfully!");
      }
      setAwsDialogOpen(false);
      fetchAwsAccounts();
    } catch (error) {
      const apiErrorMessage = error.response?.data?.errorMessages?.[0]?.message;
      if (apiErrorMessage) {
        setAwsErrors({ accountId: apiErrorMessage }); 
      } else {
        toast.error(error.response?.data?.message || "Error saving account");
      }
    } finally {
      setAwsLoading(false);
    }
  };

  const handleAwsClose = () => {
    setAwsErrors({});
    setAwsDialogOpen(false);
  };


  /* =========================================================================================
     2. GIT PIPELINE INFORMATION SETTING (CLOUD SETUP)
  ========================================================================================= */
  const [infrastructures, setInfrastructures] = useState([]);
  const [infrastructureSettings, setInfrastructureSettings] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [selectedCloudProvider, setSelectedCloudProvider] = useState('');
  const [tenantIds, setTenantIds] = useState([]);
  const [accountIds, setAccountIds] = useState([]);
  const [subscriptionKeys, setSubscriptionKeys] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedSubscriptionKey, setSelectedSubscriptionKey] = useState('');
  const [selectedInfrastructure, setSelectedInfrastructure] = useState('');
  const [isActive, setIsActive] = useState(true); 

  const [gitLoading, setGitLoading] = useState(false);
  const [gitActionLoading, setGitActionLoading] = useState(false);
  const [gitDialogOpen, setGitDialogOpen] = useState(false);
  const [gitSearchTerm, setGitSearchTerm] = useState('');
  const [gitErrors, setGitErrors] = useState({}); 
  const [gitEditErrors, setGitEditErrors] = useState({}); 

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    organizationId: '', cloudProviderId: '', tenantId: '', accountId: '', subscriptionkey: '', infrastructureId: '', settingsId: '', isActive: true
  });
  
  const [editInfrastructures, setEditInfrastructures] = useState([]);
  const [editTenantIds, setEditTenantIds] = useState([]);
  const [editAccountIds, setEditAccountIds] = useState([]);
  const [editSubscriptionKeys, setEditSubscriptionKeys] = useState([]);

  const fetchGitPipelinesData = async () => {
    setGitLoading(true);
    try {
      const settingsResponse = await api.get(`/Client/infrastructureSettings/${userId}`, {
        headers: { 'X-CORRELATION-ID': '6f46585d-76bc-4764-8656-636957083ef2' }
      });
      setInfrastructureSettings(settingsResponse.data || []);
    } catch (error) {
      console.error('Error fetching git pipeline data:', error);
    } finally {
      setGitLoading(false);
    }
  };

  useEffect(() => {
    if (selectedClient && selectedCloudProvider) {
      const providerId = cloudProviders.find(p => p.value === selectedCloudProvider)?.value;
      if (providerId) fetchClientAccountDetails(selectedClient, providerId);
    }
  }, [selectedClient, selectedCloudProvider]);

  const fetchClientAccountDetails = async (clientId, providerId) => {
    setGitActionLoading(true);
    try {
      const response = await api.get(`/Client/clientAccount/${clientId}/${providerId}`, {
        headers: { 'X-CORRELATION-ID': '6f46585d-76bc-4764-8656-636957083ef2' }
      });
      if (selectedCloudProvider === '1') {
        setTenantIds(response.data || []);
        setSelectedTenantId(''); 
      } else {
        setAccountIds(response.data || []);
        setSelectedAccountId(''); 
      }
    } catch (error) {
      console.error(error);
    } finally {
      setGitActionLoading(false);
    }
  };

  const fetchSubscriptionKeys = async (tenantId) => {
    setGitActionLoading(true);
    try {
      const response = await api.get(`/Client/SubscriptionKey/${tenantId}`, { headers: { 'X-CORRELATION-ID': tenantId }});
      setSubscriptionKeys(response.data || []);
      setSelectedSubscriptionKey('');
    } catch (error) {
      console.error(error);
    } finally {
      setGitActionLoading(false);
    }
  };

  useEffect(() => {
    if (selectedCloudProvider === '1' && selectedTenantId) fetchSubscriptionKeys(selectedTenantId);
  }, [selectedTenantId]);

  useEffect(() => {
    if (Number(editFormData.cloudProviderId) === 1 && editFormData.tenantId) {
      const fetchEditSubscriptionKeys = async () => {
        setGitActionLoading(true);
        try {
          const response = await api.get(`/Client/SubscriptionKey/${editFormData.tenantId}`, { headers: { 'X-CORRELATION-ID': editFormData.tenantId }});
          setEditSubscriptionKeys(response.data || []);
          setEditFormData(prev => {
            const keyExists = response.data?.some(item => item.value === prev.subscriptionkey);
            return {
              ...prev,
              subscriptionkey: keyExists ? prev.subscriptionkey : '' 
            };
          });

        } catch (error) {
          console.error(error);
        } finally {
          setGitActionLoading(false);
        }
      };
      fetchEditSubscriptionKeys();
    }
  }, [editFormData.tenantId, editFormData.cloudProviderId]);

  const handleCloudProviderChange = async (provider) => {
    setSelectedCloudProvider(provider);
    setGitErrors(prev => ({ ...prev, provider: '' })); 
    setTenantIds([]); setAccountIds([]); setSubscriptionKeys([]); setInfrastructures([]);
    setSelectedTenantId(''); setSelectedAccountId(''); setSelectedSubscriptionKey(''); setSelectedInfrastructure('');
    
    setGitActionLoading(true);
    try {
      const providerId = cloudProviders.find(p => p.value === provider)?.value;
      const response = await api.get(`/Client/infrastructure/${providerId}`, { headers: { 'X-CORRELATION-ID': '6f46585d-76bc-4764-8656-636957083ef2' }});
      setInfrastructures(response.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setGitActionLoading(false);
    }
  };

  const handleGitAdd = () => {
    setSelectedClient(''); setSelectedCloudProvider(''); setSelectedTenantId(''); setSelectedAccountId('');
    setSelectedSubscriptionKey(''); setSelectedInfrastructure(''); setTenantIds([]); setAccountIds([]);
    setSubscriptionKeys([]); setInfrastructures([]); setIsActive(true); 
    setGitErrors({});
    setGitDialogOpen(true);
  };

  const handleGitSave = async () => {
    setGitErrors({});
    let errors = {};
    if (!selectedClient) errors.client = "Organization is required";
    if (!selectedCloudProvider) errors.provider = "Cloud Provider is required";
    if (!selectedInfrastructure) errors.infrastructure = "Infrastructure is required";

    if (Object.keys(errors).length > 0) {
      setGitErrors(errors);
      toast.error("Please fill in required fields");
      return;
    }

    setGitActionLoading(true);
    try {
      const cloudProviderId = cloudProviders.find(p => p.value === selectedCloudProvider)?.value;
      const requestBody = {
        organizationId: selectedClient,
        cloudProviderId,
        infrastructureId: selectedInfrastructure,
        clientAccountId: selectedCloudProvider === '1' ? selectedTenantId : selectedAccountId,
        subscriptionkey: selectedCloudProvider === '1' ? selectedSubscriptionKey : null,
        isActive: isActive
      };
      const response = await api.post(`/Client/infrastructureSettings`, requestBody, {
        params: { userId },
        headers: { 'X-CORRELATION-ID': '6f46585d-76bc-4764-8656-636957083ef2', 'Content-Type': 'application/json' }
      });
      if (response.data) {
        toast.success("Configuration saved successfully!");
        setGitDialogOpen(false);
        fetchGitPipelinesData();
      } else {
        toast.error("Failed to save configuration");
      }
    } catch (error) {
      const apiErrorMessage = error.response?.data?.errorMessages?.[0]?.message;
      if (apiErrorMessage) {
        setGitErrors({ client: apiErrorMessage }); 
      } else {
        toast.error("An error occurred while saving configuration");
      }
    } finally {
      setGitActionLoading(false);
    }
  };

  const handleGitClose = () => {
    setGitErrors({});
    setGitDialogOpen(false);
  };

  const handleGitEditClick = async (row) => {
    try {
      setGitEditErrors({}); 
      const settingDetails = (await api.get(`/Client/GetInfrastructureSettingsBySettingId/${row.settingsId}`, { headers: { 'X-CORRELATION-ID': '6f46585d-76bc-4764-8656-636957083ef2' }})).data;
      setEditFormData({
        organizationId: settingDetails.organization_Id,
        cloudProviderId: settingDetails.cloudProviderId,
        tenantId: (settingDetails.cloudProviderId === 1) ? settingDetails.clientAccountId : '',
        accountId: (settingDetails.cloudProviderId === 2) ? settingDetails.clientAccountId : '',
        subscriptionkey: settingDetails.subscriptionkey || '',
        infrastructureId: settingDetails.infrastructureId,
        settingsId: settingDetails.id,
        isActive: settingDetails.isActive !== undefined ? settingDetails.isActive : true 
      });

      if (settingDetails.cloudProviderId) {
        try {
          const infraRes = await api.get(`/Client/infrastructure/${settingDetails.cloudProviderId}`, { headers: { 'X-CORRELATION-ID': '6f46585d-76bc-4764-8656-636957083ef2' }});
          setEditInfrastructures(infraRes.data || []);
        } catch (e) { setEditInfrastructures([]); }
      }
      if (settingDetails.organization_Id && settingDetails.cloudProviderId) {
        const accountData = (await api.get(`/Client/clientAccount/${settingDetails.organization_Id}/${settingDetails.cloudProviderId}`, { headers: { 'X-CORRELATION-ID': '6f46585d-76bc-4764-8656-636957083ef2' }})).data || [];
        if (settingDetails.cloudProviderId === 1) setEditTenantIds(accountData);
        else setEditAccountIds(accountData);
      }
      if (settingDetails.cloudProviderId === 1 && settingDetails.clientAccountId) {
        try {
          const subsResponse = await api.get(`/Client/SubscriptionKey/${settingDetails.clientAccountId}`, { headers: { 'X-CORRELATION-ID': settingDetails.clientAccountId }});
          setEditSubscriptionKeys(subsResponse.data || []);
        } catch (error) { setEditSubscriptionKeys([]); }
      }
      setEditDialogOpen(true);
    } catch (error) {
      toast.error("Failed to open edit dialog");
    }
  };

  const handleEditCloudProviderChange = async (newProviderId) => {
    const providerIdNum = Number(newProviderId);
    const currentOrgId = editFormData.organizationId;
    setGitEditErrors(prev => ({ ...prev, provider: '' })); 
    setEditTenantIds([]); setEditAccountIds([]); setEditSubscriptionKeys([]); setEditInfrastructures([]);
    setEditFormData(prev => ({ ...prev, cloudProviderId: providerIdNum, tenantId: '', accountId: '', subscriptionkey: '', infrastructureId: '' }));

    setGitActionLoading(true);
    try {
      const infraResponse = await api.get(`/Client/infrastructure/${providerIdNum}`, { headers: { 'X-CORRELATION-ID': '6f46585d-76bc-4764-8656-636957083ef2' }});
      setEditInfrastructures(infraResponse.data || []);
      if (!currentOrgId) return;
      const accountResponse = await api.get(`/Client/clientAccount/${currentOrgId}/${providerIdNum}`, { headers: { 'X-CORRELATION-ID': '6f46585d-76bc-4764-8656-636957083ef2' }});
      if (providerIdNum === 1) {
        setEditTenantIds(accountResponse.data || []);
      } else if (providerIdNum === 2) {
        setEditAccountIds(accountResponse.data || []);
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load account details');
    } finally {
      setGitActionLoading(false);
    }
  };

  const handleGitEditSave = async () => {
    setGitEditErrors({});
    let errors = {};
    if (!editFormData.organizationId) errors.client = "Organization is required";
    if (!editFormData.cloudProviderId) errors.provider = "Cloud Provider is required";
    if (!editFormData.infrastructureId) errors.infrastructure = "Infrastructure is required";

    if (Object.keys(errors).length > 0) {
      setGitEditErrors(errors);
      toast.error("Please fill in required fields");
      return;
    }

    setGitActionLoading(true);
    try {
      const requestBody = {
        organizationId: editFormData.organizationId,
        cloudProviderId: editFormData.cloudProviderId,
        infrastructureId: editFormData.infrastructureId,
        clientAccountId: Number(editFormData.cloudProviderId) === 1 ? editFormData.tenantId : editFormData.accountId,
        subscriptionkey: Number(editFormData.cloudProviderId) === 1 ? editFormData.subscriptionkey : null,
        isActive: editFormData.isActive, 
        createdBy: Number.parseInt(userId) || 0,
        updatedBy: Number.parseInt(userId) || 0
      };
      const response = await api.post(`/Client/UpdateInfrastructureSettingsBySettingId/${editFormData.settingsId}`, requestBody, {
        headers: { 'X-CORRELATION-ID': '6f46585d-76bc-4764-8656-636957083ef2', 'Content-Type': 'application/json'}
      });
      if (response.data) {
        toast.success("Configuration updated successfully!");
        setEditDialogOpen(false);
        fetchGitPipelinesData();
      } else {
        toast.error("Failed to update configuration");
      }
    } catch (error) {
      const apiErrorMessage = error.response?.data?.errorMessages?.[0]?.message;
      if (apiErrorMessage) {
        setGitEditErrors({ client: apiErrorMessage }); 
      } else {
        toast.error(error.response?.data?.message || "An error occurred while updating configuration");
      }
    } finally {
      setGitActionLoading(false);
    }
  };

  const handleGitEditClose = () => {
    setGitEditErrors({});
    setEditDialogOpen(false);
  };


  /* =========================================================================================
     3. PROJECT / PROVIDER SETTINGS
  ========================================================================================= */
  const [projects, setProjects] = useState([]);
  const [ppLoading, setPpLoading] = useState(false);
  const [ppSearchTerm, setPpSearchTerm] = useState('');
  
  const [selectedProjectIds, setSelectedProjectIds] = useState([]); 
  
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectFormData, setProjectFormData] = useState({
    projectId: null,
    projectName: '',
    cloudProvider: '',
    organizationId: '',
    tenantID: '',
    accountId: '',
    subscriptionId: '',
    isActive: true
  });
  const [projectErrors, setProjectErrors] = useState({}); 

  const fetchProjects = async () => {
    setPpLoading(true);
    try {
      const response = await api.get(`/Project/getProjectDetails/${userId}`, {
        params: {
          includeAllProjects: true, 
        },
      });
      setProjects(response.data || []);
    } catch (error) {
      toast.error(error.message || "Error fetching project details.");
    } finally {
      setPpLoading(false);
    }
  };

  const handleProceedClick = async () => {
    if (selectedProjectIds.length === 0) return;
    const project = projects.find(p => p.projectId === selectedProjectIds[0]);
    if (!project) return;
  
    const providerEntry = cloudProviders.find(
      p => p.text?.toLowerCase() === project.cloudProvider?.toLowerCase() || p.value === project.cloudProvider
    );
    if (!providerEntry) return;

    try {
      const providerKey = project.cloudProvider.toLowerCase();
      const handler = providerHandlers[providerKey];
      const activeAccountId = handler ? handler(project) : project.accountId;
  
      const response = await api.post(`/Account/${userId}/${activeAccountId}`);
  
      if (response.status === 200 && response.data?.success) {
        selectAccount(project, providerEntry.value, activeAccountId, response.data);
        toast.success("Active account updated successfully.");
        const provider = project.cloudProvider.toLowerCase();
        
        if (["aws", "azure", "gcp"].includes(provider)) {
          navigate('/resources', { state: { provider } });
        } else {
          toast.error("Unknown cloud provider.");
        }
      } else {
        toast.error("Failed to update active account.");
      }
    } catch (error) {
      toast.error(error.message || "Error updating active account.");
    }
  };

  const handleProjectAdd = () => {
    setProjectFormData({
      projectId: null, projectName: '', cloudProvider: '', organizationId: '',
      tenantID: '', accountId: '', subscriptionId: '', isActive: true
    });
    setProjectErrors({}); 
    setProjectDialogOpen(true);
  };

  const handleProjectEditClick = (project) => {
    let resolvedProviderValue = '';
    
    const providerEntry = cloudProviders.find(p => 
      String(p.value) === String(project.cloudProvider) || 
      String(p.text || '').toLowerCase() === String(project.cloudProvider || '').toLowerCase()
    );

    if (providerEntry) {
      resolvedProviderValue = String(providerEntry.value);
    } else {
      const raw = String(project.cloudProvider || '').toLowerCase();
      if (raw === '1' || raw === 'azure') resolvedProviderValue = '1';
      else if (raw === '2' || raw === 'aws') resolvedProviderValue = '2';
      else if (raw === '3' || raw === 'gcp') resolvedProviderValue = '3';
      else resolvedProviderValue = String(project.cloudProvider || '');
    }

    setProjectFormData({
      projectId: project.projectId,
      projectName: project.projectName || '',
      cloudProvider: resolvedProviderValue,
      organizationId: project.organizationId || '',
      tenantID: project.tenantID || '',
      accountId: project.accountId || '',
      subscriptionId: project.subscriptionId || '',
      isActive: project.isActive !== undefined ? project.isActive : true
    });

    setProjectErrors({}); 
    setProjectDialogOpen(true);
  };

  const handleProjectSave = async () => {
    setProjectErrors({});
    let errors = {};
    if (!projectFormData.organizationId) errors.organizationId = "Organization is required";
    if (!projectFormData.projectName) errors.projectName = "Project Name is required";
    if (!projectFormData.cloudProvider) errors.cloudProvider = "Cloud Provider is required";

    if (Object.keys(errors).length > 0) {
      setProjectErrors(errors);
      toast.error("Please fill in required fields.");
      return;
    }
    
    setPpLoading(true);
    
    const payload = {
      projectName: projectFormData.projectName,
      cloudSubscriptionId: projectFormData.subscriptionId,
      cloudProvider: projectFormData.cloudProvider,
      organizationId: projectFormData.organizationId,
      tenantID: projectFormData.tenantID,
      accountId: projectFormData.accountId,
      isActive: projectFormData.isActive,
      userId: userId
    };
  
    try {
      if (projectFormData.projectId) {
        const response = await api.post(`/Project/UpdateProjectDetails/${projectFormData.projectId}`, payload);
        if (response.status === 200) {
          toast.success("Project updated successfully.");
        }
      } else {
        const response = await api.post('/Project/addProject', payload, {
          headers: {
            'X-CORRELATION-ID': 'a4464b6c-0f7c-470e-a41d-b9f6cdf22563',
            'Content-Type': 'application/json',
          },
        });
        if (response.status === 200 || response.status === 201) {
          toast.success("Project added successfully.");
        }
      }
      setProjectDialogOpen(false);
      fetchProjects();
    } catch (error) {
      const apiErrorMessage = error.response?.data?.errorMessages?.[0]?.message;
      if (apiErrorMessage) {
        setProjectErrors({ projectName: apiErrorMessage });
      } else {
        toast.error(error.message || "Error saving project.");
      }
    } finally {
      setPpLoading(false);
    }
  };

  const handleProjectClose = () => {
    setProjectErrors({});
    setProjectDialogOpen(false);
  };

  useEffect(() => {
    if (activeTab === 0) {
      fetchAwsAccounts();
    } else if (activeTab === 1) {
      fetchGitPipelinesData();
    } else if (activeTab === 2) {
      fetchProjects();
    }
  }, [activeTab]);

  const awsColumns = useMemo(() => [
    {
      field: 'cloudProvider',
      headerName: 'Cloud Provider',
      flex: 1,
      valueGetter: (value, row) => cloudProviders.find((provider) => String(provider.value) === String(row?.cloudProvider))?.text || '-',
      renderCell: (params) => cloudProviders.find((provider) => String(provider.value) === String(params.row?.cloudProvider))?.text || '-',
    },
    { field: 'accountId', headerName: 'Account ID/Subscription ID', flex: 1.7 },
    {
      field: 'tenantId',
      headerName: 'Tenant ID',
      flex: 1,
      valueGetter: (value, row) => row?.tenantId || '',
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'applicationClientId',
      headerName: 'Application Client ID',
      flex: 1.4,
      valueGetter: (value, row) => row?.applicationClientId || '',
      renderCell: (params) => params.value || '-',
    },
    { field: 'accountName', headerName: 'Account Name', flex: 1.2 },
    { field: 'roleName', headerName: 'Role Name', flex: 1 },
    { field: 'environment', headerName: 'Environment', flex:1 },
    {
      field: 'isAssumeRole', headerName: 'Assume Role', flex: 0.9,
      valueGetter: (value, row) => (row?.isAssumeRole ? 'Yes' : 'No'),
      renderCell: (params) => (
        <Box component="span" sx={{
          bgcolor: params.row?.isAssumeRole ? alpha(theme.palette.info.main, 0.1) : 'transparent',
          color: params.row?.isAssumeRole ? theme.palette.info.main : 'text.secondary',
          border: `1px solid ${params.row?.isAssumeRole ? theme.palette.info.main : theme.palette.divider}`,
          borderRadius: '4px', padding: '2px 8px', fontSize: '0.8rem', fontWeight: 600,
        }}>
          {params.row?.isAssumeRole ? 'Yes' : 'No'}
        </Box>
      ),
    },
    {
      field: 'isActive', headerName: 'Status', flex: 0.7, sortable: false, disableColumnMenu: true,
      valueGetter: (value, row) => (row?.isActive ? 'Active' : 'Inactive'),
      renderHeader: () => (
        <StatusFilterHeader value={awsStatusFilter} onChange={setAwsStatusFilter} />
      ),
      renderCell: (params) => (
        <Box component="span"  sx={{ mt: 1.1, px: 1.4, py: 0.55, borderRadius: 999, fontSize: 12, fontWeight: 700,
          color: params.row?.isActive ? theme.palette.success.main : theme.palette.error.main,
          bgcolor: alpha(params.row?.isActive ? theme.palette.success.main : theme.palette.error.main, theme.palette.mode === "dark" ? 0.18 : 0.12),
        }}>
          {params.row?.isActive ? 'Active' : 'Inactive'}
        </Box>
      ),
    },
    {
      field: 'actions', headerName: 'Actions', flex: 0.6, sortable: false, disableColumnMenu: true,
      renderCell: (params) => (
        <Button variant="outlined" size="small" sx={{ borderRadius: 999, px: 2 }} onClick={(e) => { e.stopPropagation(); handleAwsEditClick(params.row); }}>
          Edit
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [theme, awsStatusFilter, cloudProviders]);

  const gitColumns = useMemo(() => [
    { field: 'organizationName', headerName: 'Organization Name', flex: 1 },
    { field: 'providerName', headerName: 'Provider', flex: 1 },
    {
      field: 'infrastructure', headerName: 'IaC', flex: 1, renderCell: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {getIcon(params.value) && <img width={14} src={getIcon(params.value)} alt={params.value} />}
          <span>{params.value || '-'}</span>
        </div>
      ),
    },
    { field: 'clientAccountId', headerName: 'Account ID/Tenant ID', flex: 1 },
    { field: 'subscriptionkey', headerName: 'Subscription Key', flex: 1, renderCell: (params) => params.value ? params.value : '-' },
    {
      field: 'isActive', headerName: 'Status', flex: 0.7, sortable: false, disableColumnMenu: true,
      valueGetter: (value, row) => (row?.isActive ? 'Active' : 'Inactive'),
      renderHeader: () => (
        <StatusFilterHeader value={gitStatusFilter} onChange={setGitStatusFilter} />
      ),
      renderCell: (params) => (
        <Box component="span"  sx={{ mt: 1.1, px: 1.4, py: 0.55, borderRadius: 999, fontSize: 12, fontWeight: 700,
          color: params.row?.isActive ? theme.palette.success.main : theme.palette.error.main,
          bgcolor: alpha(params.row?.isActive ? theme.palette.success.main : theme.palette.error.main, theme.palette.mode === "dark" ? 0.18 : 0.12),
        }}>
          {params.row?.isActive ? 'Active' : 'Inactive'}
        </Box>
      ),
    },
    {
      field: 'actions', headerName: 'Actions', flex: 0.6, sortable: false, disableColumnMenu: true,
      renderCell: (params) => (
        <Button variant="outlined" size="small" sx={{ borderRadius: 999, px: 2 }} onClick={(e) => { e.stopPropagation(); handleGitEditClick(params.row); }}>
          Edit
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [theme, gitStatusFilter]);

  const projectColumns = useMemo(() => [
    { field: 'organizationName', headerName: 'Organization Name', flex: 1, 
      renderCell: (params) => params.row.organizationName 
    },
    { field: 'projectName', headerName: 'Project Name', flex: 1 },
    { field: 'cloudProvider', headerName: 'Provider', flex: 1 },
    { field: 'accountOrTenant', headerName: 'Account ID', flex: 1, 
      valueGetter: (value, row) => row?.accountId || row?.tenantID || '',
      renderCell: (params) => params.row.accountId || params.row.tenantID 
    },
    { field: 'subscriptionId', headerName: 'Subscription ID', flex: 1, 
      renderCell: (params) => params.row.subscriptionId
    },
      
    {
      field: 'isActive', headerName: 'Status', flex: 0.7, sortable: false, disableColumnMenu: true,
      valueGetter: (value, row) => (row?.isActive ? 'Active' : 'Inactive'),
      renderHeader: () => (
        <StatusFilterHeader value={ppStatusFilter} onChange={setPpStatusFilter} />
      ),
      renderCell: (params) => (
        <Box component="span"  sx={{ mt: 1.1, px: 1.4, py: 0.55, borderRadius: 999, fontSize: 12, fontWeight: 700,
          color: params.row?.isActive ? theme.palette.success.main : theme.palette.error.main,
          bgcolor: alpha(params.row?.isActive ? theme.palette.success.main : theme.palette.error.main, theme.palette.mode === "dark" ? 0.18 : 0.12),
        }}>
          {params.row?.isActive ? 'Active' : 'Inactive'}
        </Box>
      )
    },
    {
      field: 'actions', headerName: 'Actions', flex: 0.6, sortable: false, disableColumnMenu: true,
      renderCell: (params) => (
        <Button variant="outlined" size="small" sx={{ borderRadius: 999, px: 2 }} onClick={(e) => { e.stopPropagation(); handleProjectEditClick(params.row); }}>
          Edit
        </Button>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [theme, ppStatusFilter]);

  // Search across all data columns, then apply the per-tab Status filter
  const filteredAwsAccounts = useMemo(
    () => applyStatusFilter(filterRowsByColumns(awsAccounts, awsSearchTerm, awsColumns), awsStatusFilter),
    [awsSearchTerm, awsAccounts, awsColumns, awsStatusFilter]
  );

  const filteredGitSettings = useMemo(
    () => applyStatusFilter(filterRowsByColumns(infrastructureSettings, gitSearchTerm, gitColumns), gitStatusFilter),
    [gitSearchTerm, infrastructureSettings, gitColumns, gitStatusFilter]
  );

  const filteredProjects = useMemo(
    () => applyStatusFilter(filterRowsByColumns(projects, ppSearchTerm, projectColumns), ppStatusFilter),
    [ppSearchTerm, projects, projectColumns, ppStatusFilter]
  );

  const gridStyles = {
    border: "none",
    bgcolor: (t) => t.palette.mode === "dark" ? "background.paper" : "#ffffff",
    "& .MuiCheckbox-root.Mui-checked .MuiSvgIcon-root, & .MuiCheckbox-root.MuiCheckbox-indeterminate .MuiSvgIcon-root, & .MuiCheckbox-root.Mui-indeterminate .MuiSvgIcon-root": { color: (t) => `${t.palette.primary.main} !important` },
    "& .MuiCheckbox-root .MuiSvgIcon-root": { color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.54)" },
    "& .MuiCheckbox-root.Mui-disabled .MuiSvgIcon-root": { color: (t) => `${t.palette.action.disabled} !important` },
    "--DataGrid-rowBorderColor": "transparent",
    "& .MuiDataGrid-filler": { display: "none" },
    "& .MuiDataGrid-columnHeaders": { bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff", color: "text.primary", borderBottom: "none", minWidth: "max-content" },
    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: "bold !important", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.4 },
    "& .MuiDataGrid-columnSeparator": { color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)" },
    "& .MuiDataGrid-columnHeader .MuiSvgIcon-root:not(.MuiCheckbox-root .MuiSvgIcon-root)": { color: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.11)" : "rgba(0, 0, 0, 0.2)" },
    "& .MuiDataGrid-cell": { color: "text.primary", borderBottom: "none !important" },
    "& .MuiDataGrid-row:nth-of-type(even)": { bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "#fafafa" },
    "& .MuiDataGrid-row:nth-of-type(odd)": { bgcolor: (t) => t.palette.mode === "dark" ? "transparent" : "#ffffff" },
    "& .MuiDataGrid-row:hover": { bgcolor: (t) => t.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08) !important" : "#e8f0fe !important" },
    "& .MuiDataGrid-row.Mui-selected": { bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.16) !important" : "#e3f2fd !important" },
    "& .MuiDataGrid-row.Mui-selected:hover": { bgcolor: (t) => t.palette.mode === "dark" ? "rgba(144, 202, 249, 0.24) !important" : "#d0eafb !important" },
    "& .MuiDataGrid-footerContainer": { bgcolor: (t) => t.palette.mode === "dark" ? "background.default" : "#ffffff", borderTop: "none", color: "text.primary" },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": { outline: "none" },
    "& .MuiDataGrid-main": { overflowX: "auto" },
    "& .MuiDataGrid-virtualScrollerContent": { paddingBottom: "16px", minWidth: "max-content" },
    "& .MuiDataGrid-virtualScroller": { overflowX: "auto" }
  };

  const subTabStyles = {
  minHeight: 40,
  borderRadius: '12px',
  textTransform: "none",
  fontWeight: 600,
  color: 'text.secondary',
  zIndex: 1, 
  transition: "all 0.2s ease",
  padding: "8px 16px",
  
  "&.Mui-selected": {
    color: 'text.primary', 
  },
};

  const GRID_MIN_WIDTH = 1400;

  return (
    <Box sx={{ flex: 1, color: theme.palette.text.primary }}>

      {/* TABS NAVIGATION */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Tabs
  value={activeTab}
  onChange={handleTabChange}
  TabIndicatorProps={{
    sx: {
      height: "100%",
      borderRadius: "12px",
      backgroundColor: (theme) => theme.palette.mode === "dark"
        ? "rgba(127, 178, 255, 0.18)" 
        : "rgba(43, 97, 170, 0.12)",  
      zIndex: 0,
      pointerEvents: "none", 
    }
  }}
  sx={{
    minHeight: 52,
    backgroundColor: (theme) => theme.palette.mode === "dark"
      ? "rgba(255, 255, 255, 0.04)"
      : "rgba(43, 97, 170, 0.04)",
    borderRadius: '16px',
    padding: '6px',
    border: (theme) => `1px solid ${theme.palette.divider}`,
    display: 'inline-flex',
    '& .MuiTabs-flexContainer': {
      gap: '6px',
    }
  }}
>
  <Tab disableRipple label="Cloud Account Details" value={0} sx={subTabStyles} />
  <Tab disableRipple label="Cloud Information Settings" value={1} sx={subTabStyles} />
  <Tab disableRipple label="Project / Provider" value={2} sx={subTabStyles} />
</Tabs>

        {/* Dynamic Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pb: 1 }}>
          {activeTab === 0 && (
            <>
              <SearchComponent value={awsSearchTerm} onSearchChange={setAwsSearchTerm} placeholder="Search accounts..." />
              <Button variant="contained" sx={{ borderRadius: 3, px: 3, py: 1, whiteSpace: 'nowrap' }} onClick={handleAwsAdd}>
                Add Account
              </Button>
            </>
          )}

          {activeTab === 1 && (
            <>
              <SearchComponent value={gitSearchTerm} onSearchChange={setGitSearchTerm} placeholder="Search settings..." />
              <Button variant="contained" sx={{ borderRadius: 3, px: 3, py: 1, whiteSpace: 'nowrap' }} onClick={handleGitAdd}>
                Configure Settings
              </Button>
            </>
          )}

          {activeTab === 2 && (
            <>
              <SearchComponent value={ppSearchTerm} onSearchChange={setPpSearchTerm} placeholder="Search projects..." />
              
                <Button variant="contained" sx={{ borderRadius: 3, px: 3, py: 1, whiteSpace: 'nowrap' }} onClick={handleProjectAdd}>
                  Add Project
                </Button>
              
            </>
          )}
        </Box>
      </Box>

      {/* 1. AWS ACCOUNT DETAILS */}
      <CustomTabPanel value={activeTab} index={0}>
        <Box sx={{height: 'calc(100vh - 310px)', minHeight: 380, width: '100%', overflowX: 'auto' }}>
          <DataGrid 
            rows={filteredAwsAccounts} 
            columns={awsColumns} 
            pageSize={5} 
            rowsPerPageOptions={[5]} 
            disableSelectionOnClick 
            hideFooterSelectedRowCount 
            getRowId={(row) => row.cloudAccountDetailId ?? row.awsAccountDetailId ?? row.accountId} 
            sx={{ ...gridStyles, minWidth: GRID_MIN_WIDTH }}
            loading={awsLoading}
          />
        </Box>
      </CustomTabPanel>

      {/* 2. Configure Settings */}
      <CustomTabPanel value={activeTab} index={1}>
        <Box sx={{ height: 'calc(100vh - 310px)', minHeight: 380, width: '100%', overflowX: 'auto' }}>
          {gitLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>
          ) : (
            <DataGrid rows={filteredGitSettings} columns={gitColumns} pageSize={20} rowsPerPageOptions={[10, 20, 50]} disableSelectionOnClick hideFooterSelectedRowCount getRowId={(row) => row.settingsId} sx={{ ...gridStyles, minWidth: GRID_MIN_WIDTH }} />
          )}
        </Box>
      </CustomTabPanel>

      {/* PROJECT / PROVIDER TAB */}
      <CustomTabPanel value={activeTab} index={2}>
        <Box sx={{ height: 'calc(100vh - 310px)', minHeight: 380, width: '100%', overflowX: 'auto' }}>
          {ppLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>
          ) : (
            <DataGrid 
              rows={filteredProjects} 
              columns={projectColumns} 
              pageSize={5} 
              rowsPerPageOptions={[5]} 
              disableSelectionOnClick 
              hideFooterSelectedRowCount 
              getRowId={(row) => row.projectId} 
              sx={{ ...gridStyles, minWidth: GRID_MIN_WIDTH }} 
            />
          )}
        </Box>
      </CustomTabPanel>


      {/* =======================================================================================
         MODALS / DIALOGS
      ======================================================================================= */}

      {/* 1. AWS ACCOUNT DIALOG */}
      <Dialog open={awsDialogOpen} onClose={handleAwsClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: theme.palette.background.default, pb: 1, mb: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>{awsEditId ? 'Edit Account' : 'Add Account'}</Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          <TextField 
            select
            label="Cloud Provider *" 
            fullWidth 
            value={awsFormData.cloudProvider}
            sx={{mt:2}} 
            onChange={(e) => {
              const nextProvider = e.target.value;
              const isAzureProvider = String(nextProvider) === azureProviderValue;
              setAwsFormData({
                ...awsFormData,
                cloudProvider: nextProvider,
                tenantId: isAzureProvider ? awsFormData.tenantId : '',
                applicationClientId: isAzureProvider ? awsFormData.applicationClientId : '',
              });
              setAwsErrors(prev => ({...prev, cloudProvider: '', tenantId: '', applicationClientId: ''}));
            }}
            error={!!awsErrors.cloudProvider}
            helperText={awsErrors.cloudProvider}
          >
            {accountCloudProviders.map((provider) => (
              <MenuItem key={provider.value} value={provider.value}>{provider.text}</MenuItem>
            ))}
          </TextField>

          <TextField 
            label={String(awsFormData.cloudProvider) === azureProviderValue ? "Account ID/Subscription ID *" : "Account ID *"} 
            fullWidth 
            value={awsFormData.accountId} 
            onChange={(e) => {
              setAwsFormData({...awsFormData, accountId: e.target.value});
              setAwsErrors(prev => ({...prev, accountId: ''}));
            }}
            error={!!awsErrors.accountId}
            helperText={awsErrors.accountId}
          />

          {String(awsFormData.cloudProvider) === azureProviderValue && (
            <>
              <TextField
                label="Tenant ID *"
                fullWidth
                value={awsFormData.tenantId}
                onChange={(e) => {
                  setAwsFormData({...awsFormData, tenantId: e.target.value});
                  setAwsErrors(prev => ({...prev, tenantId: ''}));
                }}
                error={!!awsErrors.tenantId}
                helperText={awsErrors.tenantId}
              />
              <TextField
                label="Application Client ID *"
                fullWidth
                value={awsFormData.applicationClientId}
                onChange={(e) => {
                  setAwsFormData({...awsFormData, applicationClientId: e.target.value});
                  setAwsErrors(prev => ({...prev, applicationClientId: ''}));
                }}
                error={!!awsErrors.applicationClientId}
                helperText={awsErrors.applicationClientId}
              />
            </>
          )}

          <TextField 
            label="Account Name *" 
            fullWidth 
            value={awsFormData.accountName} 
            onChange={(e) => {
              setAwsFormData({...awsFormData, accountName: e.target.value});
              setAwsErrors(prev => ({...prev, accountName: ''})); 
            }} 
            error={!!awsErrors.accountName}
            helperText={awsErrors.accountName}
          />
          <TextField label="Role Name" fullWidth value={awsFormData.roleName} onChange={(e) => setAwsFormData({...awsFormData, roleName: e.target.value})} />
          <TextField label="Environment" fullWidth value={awsFormData.environment} onChange={(e) => setAwsFormData({...awsFormData, environment: e.target.value})} />
          
          <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
            <FormControlLabel control={<Checkbox checked={awsFormData.isAssumeRole} onChange={(e) => setAwsFormData({...awsFormData, isAssumeRole: e.target.checked})} color="primary" />} label={<Typography sx={{ fontWeight: 600 }}>Assume Role</Typography>} />
            <FormControlLabel control={<Checkbox checked={awsFormData.isActive} onChange={(e) => setAwsFormData({...awsFormData, isActive: e.target.checked})} color="primary" />} label={<Typography sx={{ fontWeight: 600 }}>Active</Typography>} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, justifyContent: 'flex-end' }}>
          <Button onClick={handleAwsClose} variant="outlined" sx={{ p: 0.7, px: 2, color: isDark ? 'grey.300' : 'text.secondary', borderColor: isDark ? 'grey.700' : 'grey.400', '&:hover': { bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)', borderColor: isDark ? 'grey.500' : 'grey.600', }}}>Cancel</Button>
          <Button onClick={handleAwsSave} variant="contained" color="primary" sx={{py:1.2}} disabled={awsLoading}>{awsLoading ? 'Saving...' : 'Save Account'}</Button>
        </DialogActions>
      </Dialog>

      {/* 2. GIT PIPELINE ADD DIALOG */}
      <Dialog open={gitDialogOpen} onClose={handleGitClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: theme.palette.background.default, pb: 1, mb: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>Add Cloud Information</Typography>
        </DialogTitle>
        <DialogContent sx={{display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          <TextField 
            select 
            fullWidth 
            label="Organization *" 
            value={selectedClient} 
            sx={{mt:2}} 
            onChange={(e) => {
              setSelectedClient(e.target.value);
              setGitErrors(prev => ({...prev, client: ''}));
            }}
            error={!!gitErrors.client}
            helperText={gitErrors.client}
          >
            {activeClients.map(client => <MenuItem key={client.organizationId} value={client.organizationId}>{client.organizationName}</MenuItem>)}
          </TextField>
          
          <TextField 
            select 
            fullWidth 
            label="Cloud Provider *" 
            value={selectedCloudProvider} 
            onChange={(e) => handleCloudProviderChange(e.target.value)}
            error={!!gitErrors.provider}
            helperText={gitErrors.provider}
          >
            {cloudProviders?.map(provider => <MenuItem key={provider.value} value={provider.value}>{provider.text}</MenuItem>)}
          </TextField>
          
          {selectedCloudProvider === '1' && (
            <>
              <TextField select fullWidth label="Tenant ID" value={selectedTenantId} onChange={(e) => setSelectedTenantId(e.target.value)}>
                {tenantIds?.map(tenant => <MenuItem key={tenant.value} value={tenant.value}>{tenant.text}</MenuItem>)}
              </TextField>
              <TextField select fullWidth label="Subscription Key" value={selectedSubscriptionKey} onChange={(e) => setSelectedSubscriptionKey(e.target.value)}>
                {subscriptionKeys?.map(key => <MenuItem key={key.value} value={key.value}>{key.text}</MenuItem>)}
              </TextField>
            </>
          )}
          {selectedCloudProvider === '2' && (
            <TextField select fullWidth label="Account ID" value={selectedAccountId} onChange={(e) => setSelectedAccountId(e.target.value)}>
              {accountIds?.map(account => <MenuItem key={account.value} value={account.value}>{account.text}</MenuItem>)}
            </TextField>
          )}

          {infrastructures?.length > 0 && (
            <> 
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold' }}>Infrastructure *</Typography> 
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {infrastructures.map((option) => (
                  <Box 
                    key={option.value} 
                    onClick={() => {
                      setSelectedInfrastructure(option.value);
                      setGitErrors(prev => ({...prev, infrastructure: ''}));
                    }} 
                    sx={{ 
                      border: selectedInfrastructure === option.value ? `2px solid ${theme.palette.primary.main}` : (gitErrors.infrastructure ? `1px solid ${theme.palette.error.main}` : "1px solid #ccc"), 
                      borderRadius: "12px", 
                      padding: "5px 14px", 
                      cursor: "pointer", 
                      backgroundColor: selectedInfrastructure === option.value ? alpha(theme.palette.primary.main, 0.1) : "transparent", 
                      "&:hover": { borderColor: theme.palette.primary.main } 
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {getIcon(option.text) && <img width={14} src={getIcon(option.text)} alt={option.text} />}<span>{option.text}</span>
                    </div>
                  </Box>
                ))}
              </Box>
              {gitErrors.infrastructure && (
                <FormHelperText error>{gitErrors.infrastructure}</FormHelperText>
              )}
            </>
          )}
          <Box>
            <FormControlLabel control={<Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} color="primary" />} label={<Typography sx={{ fontWeight: 600 }}>Active</Typography>} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, justifyContent: 'flex-end' }}>
          <Button onClick={handleGitClose} variant="outlined" sx={{ p: 0.7, px: 2, color: isDark ? 'grey.300' : 'text.secondary', borderColor: isDark ? 'grey.700' : 'grey.400', '&:hover': { bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)', borderColor: isDark ? 'grey.500' : 'grey.600', }}}>Cancel</Button>
          <Button onClick={handleGitSave} variant="contained" color="primary" sx={{py:1.2}} disabled={gitActionLoading}>{gitActionLoading ? 'Saving...' : 'Save Configuration'}</Button>
        </DialogActions>
      </Dialog>

      {/* GIT PIPELINE EDIT DIALOG */}
      <Dialog open={editDialogOpen} onClose={handleGitEditClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: theme.palette.background.default, pb: 1, mb: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>Edit Cloud Information</Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
          <TextField 
            sx={{mt:2}} 
            select 
            fullWidth 
            label="Organization *" 
            value={editFormData.organizationId || ''} 
            onChange={(e) => {
              setEditFormData({ ...editFormData, organizationId: e.target.value });
              setGitEditErrors(prev => ({...prev, client: ''}));
            }}
            error={!!gitEditErrors.client}
            helperText={gitEditErrors.client}
          >
            {activeClients.map(client => <MenuItem key={client.organizationId} value={client.organizationId}>{client.organizationName}</MenuItem>)}
          </TextField>
          
          <TextField 
            select 
            fullWidth 
            label="Cloud Provider *" 
            value={editFormData.cloudProviderId || ''} 
            onChange={(e) => handleEditCloudProviderChange(e.target.value)}
            error={!!gitEditErrors.provider}
            helperText={gitEditErrors.provider}
          >
            {cloudProviders?.map(provider => <MenuItem key={provider.value} value={provider.value}>{provider.text}</MenuItem>)}
          </TextField>

          {Number(editFormData.cloudProviderId) === 1 && (
            <>
              <TextField select fullWidth label="Tenant ID" value={editFormData.tenantId || ''} onChange={(e) => setEditFormData(prev => ({ ...prev, tenantId: e.target.value }))}>
                {editTenantIds?.length > 0 ? editTenantIds.map(tenant => <MenuItem key={tenant.value} value={tenant.value}>{tenant.text}</MenuItem>) : <MenuItem disabled>No Tenant IDs available</MenuItem>}
              </TextField>
              {editFormData.tenantId && (
                <TextField select fullWidth label="Subscription Key" value={editFormData.subscriptionkey || ''} onChange={(e) => setEditFormData({ ...editFormData, subscriptionkey: e.target.value })}>
                  {editSubscriptionKeys?.length > 0 ? editSubscriptionKeys.map(key => <MenuItem key={key.value} value={key.value}>{key.text}</MenuItem>) : <MenuItem disabled>Loading Subscription Keys...</MenuItem>}
                </TextField>
              )}
            </>
          )}
          {Number(editFormData.cloudProviderId) === 2 && (
            <TextField select fullWidth label="Account ID" value={editFormData.accountId || ''} onChange={(e) => setEditFormData({ ...editFormData, accountId: e.target.value })}>
              {editAccountIds?.length > 0 ? editAccountIds.map(account => <MenuItem key={account.value} value={account.value}>{account.text}</MenuItem>) : <MenuItem disabled>Loading Account IDs...</MenuItem>}
            </TextField>
          )}

          {editInfrastructures?.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 'bold' }}>Infrastructure *</Typography> 
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                {editInfrastructures.map((option) => (
                  <Box 
                    key={option.value} 
                    onClick={() => {
                      setEditFormData({ ...editFormData, infrastructureId: option.value });
                      setGitEditErrors(prev => ({...prev, infrastructure: ''}));
                    }} 
                    sx={{ 
                      border: String(editFormData.infrastructureId) === String(option.value) ? `2px solid ${theme.palette.primary.main}` : (gitEditErrors.infrastructure ? `1px solid ${theme.palette.error.main}` : "1px solid #ccc"), 
                      borderRadius: "12px", 
                      padding: "5px 14px", 
                      cursor: "pointer", 
                      backgroundColor: String(editFormData.infrastructureId) === String(option.value) ? alpha(theme.palette.primary.main, 0.1) : "transparent", 
                      "&:hover": { borderColor: theme.palette.primary.main } 
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {getIcon(option.text) && <img width={14} src={getIcon(option.text)} alt={option.text} />}<span>{option.text}</span>
                    </div>
                  </Box>
                ))}
              </Box>
              {gitEditErrors.infrastructure && (
                <FormHelperText error>{gitEditErrors.infrastructure}</FormHelperText>
              )}
            </>
          )}
          <Box>
            <FormControlLabel control={<Checkbox checked={editFormData.isActive} onChange={(e) => setEditFormData({ ...editFormData, isActive: e.target.checked })} color="primary" />} label={<Typography sx={{ fontWeight: 600 }}>Active</Typography>} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, justifyContent: 'flex-end' }}>
          <Button onClick={handleGitEditClose} variant="outlined" sx={{ p: 0.7, px: 2, color: isDark ? 'grey.300' : 'text.secondary', borderColor: isDark ? 'grey.700' : 'grey.400', '&:hover': { bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)', borderColor: isDark ? 'grey.500' : 'grey.600', }}}>Cancel</Button>
          <Button onClick={handleGitEditSave} variant="contained" color="primary" sx={{py:1.2}} disabled={gitActionLoading}>{gitActionLoading ? 'Updating...' : 'Update Configuration'}</Button>
        </DialogActions>
      </Dialog>


      {/* 3. PROJECT / PROVIDER ADD/EDIT DIALOG (Unified) */}
      <Dialog open={projectDialogOpen} onClose={handleProjectClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: theme.palette.background.default, pb: 1, mb: 2 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: "text.primary" }}>
            {projectFormData.projectId ? 'Edit Project' : 'Add Project'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2}}>
          
          <TextField 
            select 
            fullWidth 
            label="Organization *" 
            value={projectFormData.organizationId} sx={{mt:2}}
            onChange={(e) => {
              setProjectFormData({ ...projectFormData, organizationId: e.target.value });
              setProjectErrors(prev => ({...prev, organizationId: ''}));
            }}
            error={!!projectErrors.organizationId}
            helperText={projectErrors.organizationId}
          >
            {activeClients.map(client => (
              <MenuItem key={client.organizationId} value={client.organizationId}>{client.organizationName}</MenuItem>
            ))}
          </TextField>

          <TextField 
            label="Project Name *" 
            value={projectFormData.projectName} 
            fullWidth 
            onChange={(e) => {
              setProjectFormData({ ...projectFormData, projectName: e.target.value });
              setProjectErrors(prev => ({...prev, projectName: ''}));
            }}
            error={!!projectErrors.projectName}
            helperText={projectErrors.projectName}
          />
          
          <TextField
            select
            fullWidth
            label="Cloud Provider *"
            value={projectFormData.cloudProvider}
            onChange={(e) => {
              setProjectFormData({ ...projectFormData, cloudProvider: e.target.value });
              setProjectErrors(prev => ({...prev, cloudProvider: ''}));
            }}
            error={!!projectErrors.cloudProvider}
            helperText={projectErrors.cloudProvider}
          >
            {cloudProviders.map((provider) => (
              <MenuItem key={provider.value} value={provider.value}>{provider.text}</MenuItem>
            ))}
          </TextField>

          {String(projectFormData.cloudProvider) === "2" && ( 
            <TextField 
              label="Account ID" 
              fullWidth 
              value={projectFormData.accountId}
              onChange={(e) => setProjectFormData({ ...projectFormData, accountId: e.target.value })}
            />
          )}

          {String(projectFormData.cloudProvider) === "1" && ( 
            <>
              <TextField 
                label="Tenant ID" 
                fullWidth 
                value={projectFormData.tenantID}
                onChange={(e) => setProjectFormData({ ...projectFormData, tenantID: e.target.value })}
              />
              <TextField 
                label="Subscription ID" 
                fullWidth 
                value={projectFormData.subscriptionId}
                onChange={(e) => setProjectFormData({ ...projectFormData, subscriptionId: e.target.value })}
              />
            </>
          )}

          {String(projectFormData.cloudProvider) === "3" && ( 
            <TextField 
              label="Project ID / Account ID" 
              fullWidth 
              value={projectFormData.accountId}
              onChange={(e) => setProjectFormData({ ...projectFormData, accountId: e.target.value })}
            />
          )}

          <Box>
            <FormControlLabel 
              control={<Checkbox checked={projectFormData.isActive} onChange={(e) => setProjectFormData({ ...projectFormData, isActive: e.target.checked })} color="primary" />} 
              label={<Typography sx={{ fontWeight: 600 }}>Active</Typography>} 
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1, justifyContent: 'flex-end' }}>
          <Button onClick={handleProjectClose} variant="outlined" sx={{ p: 0.7, px: 2, color: isDark ? 'grey.300' : 'text.secondary', borderColor: isDark ? 'grey.700' : 'grey.400', '&:hover': { bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)', borderColor: isDark ? 'grey.500' : 'grey.600', }}}>Cancel</Button>
          <Button onClick={handleProjectSave} variant="contained" color="primary" sx={{py:1.2}} disabled={ppLoading}>
            {ppLoading ? 'Saving...' : 'Save Details'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}

export default CloudSetup;